import { useCallback, useRef, useState } from 'react'
import { Device, Call } from '@twilio/voice-sdk'
import { useMonitorMutation } from '../../api/monitoring'

export type MonitorState = 'idle' | 'connecting' | 'connected' | 'error'

interface UseMonitorReturn {
  monitor: (callId: string) => void
  disconnect: () => void
  state: MonitorState
  isConnected: boolean
  isConnecting: boolean
  error: string | null
  conferenceName: string | null
  dialInNumber: string | null
}

export function useMonitor(): UseMonitorReturn {
  const [state, setState] = useState<MonitorState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [conferenceName, setConferenceName] = useState<string | null>(null)
  const [dialInNumber, setDialInNumber] = useState<string | null>(null)

  const deviceRef = useRef<Device | null>(null)
  const callRef = useRef<Call | null>(null)
  const monitorMutation = useMonitorMutation()

  const cleanup = useCallback(() => {
    if (callRef.current) {
      callRef.current.disconnect()
      callRef.current = null
    }
    if (deviceRef.current) {
      deviceRef.current.destroy()
      deviceRef.current = null
    }
  }, [])

  const monitor = useCallback(
    async (callId: string) => {
      cleanup()
      setState('connecting')
      setError(null)

      try {
        const data = await monitorMutation.mutateAsync(callId)

        setConferenceName(data.conference_name)
        setDialInNumber(data.dial_in_number)

        const device = new Device(data.token, {
          codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
          closeProtection: true,
        })
        deviceRef.current = device

        await device.register()

        const call = await device.connect({
          params: { conference_name: data.conference_name },
        })
        callRef.current = call

        call.on('accept', () => {
          setState('connected')
        })

        call.on('disconnect', () => {
          setState('idle')
          cleanup()
        })

        call.on('error', (err: { message?: string }) => {
          setError(err.message || 'Call error')
          setState('error')
          cleanup()
        })

        call.on('cancel', () => {
          setState('idle')
          cleanup()
        })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to start monitoring'
        setError(message)
        setState('error')
        cleanup()
      }
    },
    [monitorMutation, cleanup],
  )

  const disconnect = useCallback(() => {
    if (callRef.current) {
      callRef.current.disconnect()
    }
    setState('idle')
    cleanup()
  }, [cleanup])

  return {
    monitor,
    disconnect,
    state,
    isConnected: state === 'connected',
    isConnecting: state === 'connecting',
    error,
    conferenceName,
    dialInNumber,
  }
}
